'use client'

import { useEffect, useMemo, type Dispatch } from 'react'
import { captureEvent } from '@/instrumentation-client'
import { fetchSpeech, restUntilFrom } from '@/lib/santa-chat/api'
import type { ChatAction } from '@/lib/santa-chat/chat-state'
import { gestureClip } from '@/lib/santa-chat/gestures.manifest'
import type { SantaGesture } from '@/server/domain/santa-chat'

/**
 * La voz de una nota: su `src` reproducible (URL publicada o data URI) y, en el
 * saludo, la cola enlatada que suena justo después. Una cola sin voz no se
 * puede representar: sin `src` no hay `NoteVoice`.
 */
export interface NoteVoice {
  src: string
  tail: string | null
}

export interface SantaReply {
  id: string
  gesture: SantaGesture | null
  /** `null` si la voz no llegó. Ver `voiceFromBase64` y `voiceFromUrl`. */
  voice: NoteVoice | null
}

/** La voz en vivo (mp3 en base64, `''` si falló) y su cola, si la tiene; sin voz, tampoco cola. */
export function voiceFromBase64(audioBase64: string, tail: string | null = null): NoteVoice | null {
  return audioBase64 ? { src: `data:audio/mpeg;base64,${audioBase64}`, tail } : null
}

/** Un mp3 publicado (enlatado), sin cola. */
export function voiceFromUrl(src: string): NoteVoice {
  return { src, tail: null }
}

/**
 * De dónde sale la voz de una nota que no está en memoria. Para que Papá Noel
 * «descanse», lanza el `RATE_LIMITED` de la API (`restUntilFrom` lo reconoce).
 */
export type SpeechSource = (id: string) => Promise<{ audioBase64: string; tailAudioUrl?: string }>

/** Lo que dio pedir una voz: la voz, ninguna (suena mudo) o esperar. */
type FetchedVoice = { kind: 'voice'; voice: NoteVoice } | { kind: 'silent' } | { kind: 'rest' }

/** Lo que dura un mp3 según sus metadatos, sin sonarlo; `NaN` si el navegador no lo dice. */
function probeDuration(src: string): Promise<number> {
  if (typeof Audio === 'undefined') return Promise.resolve(Number.NaN)
  return new Promise((resolve) => {
    const probe = new Audio()
    probe.preload = 'metadata'
    probe.addEventListener('loadedmetadata', () => resolve(probe.duration), { once: true })
    probe.addEventListener('error', () => resolve(Number.NaN), { once: true })
    probe.src = src
  })
}

/**
 * Suena `src` en `audio` hasta su `ended`. Si otra reproducción lo reemplaza,
 * la promesa se resuelve con el `ended` de la siguiente; quien la espera
 * comprueba su turno antes de tocar el estado.
 */
async function playToEnd(audio: HTMLAudioElement, src: string, onStarted?: () => void): Promise<void> {
  audio.src = src
  const finished = new Promise<void>((resolve) => {
    audio.addEventListener('ended', () => resolve(), { once: true })
  })
  await audio.play()
  onStarted?.()
  await finished
}

interface PlaybackStage {
  src: string
  /** Al entrar en la etapa, antes de cargar su `src`: lo que dependa de qué fuente suena. */
  onEnter?: () => void
  /** Cuando ya suena (`play()` resuelto). */
  onStarted?: () => void
}

interface StageHooks {
  onVoiceStarted: () => void
  onTailEntered: () => void
  onTailStarted: () => void
}

/** La risa del gesto (si la tiene), después la voz (si llegó) y, tras ella, la cola enlatada del saludo. */
function replyStages(reply: SantaReply, gesture: SantaGesture, hooks: StageHooks): PlaybackStage[] {
  const stages: PlaybackStage[] = []
  const laugh = gestureClip(gesture)?.audioSrc
  if (laugh) stages.push({ src: laugh })
  if (!reply.voice) return stages
  stages.push({ src: reply.voice.src, onStarted: hooks.onVoiceStarted })
  if (reply.voice.tail) stages.push({ src: reply.voice.tail, onEnter: hooks.onTailEntered, onStarted: hooks.onTailStarted })
  return stages
}

/**
 * Las dos partes de una nota con cola (voz en vivo + mp3 enlatado) como una
 * sola: el progreso y la duración suman las dos. Sin cola, `tailSeconds` es 0.
 */
class NoteSpan {
  private headSeconds = Number.NaN
  private inTail = false

  constructor(private tailSeconds: number) {}

  headStarted(audio: HTMLAudioElement): void {
    this.headSeconds = audio.duration
  }

  /**
   * Se llama al ENTRAR en la cola, antes de asignar su `src`: el cambio de
   * fuente dispara `timeupdate`/`durationchange` antes de que suene, y esos
   * ticks ya tienen que contar desde el final de la voz.
   */
  enterTail(): void {
    this.inTail = true
  }

  tailMeasured(seconds: number): void {
    if (Number.isFinite(seconds)) this.tailSeconds = seconds
  }

  elapsed(audio: HTMLAudioElement): number {
    return this.inTail ? this.headSeconds + audio.currentTime : audio.currentTime
  }

  /** `NaN` mientras alguna de las dos partes no diga lo que dura. */
  duration(audio: HTMLAudioElement): number {
    if (!this.inTail) return audio.duration + this.tailSeconds
    return this.headSeconds + (Number.isFinite(audio.duration) ? audio.duration : this.tailSeconds)
  }

  /** La duración ya conocida sin leer el `<audio>` (para cuando llega tarde la de la cola). */
  knownDuration(): number {
    return this.headSeconds + this.tailSeconds
  }
}

/** Por dónde va la voz que suena: `id` `null` cuando no suena ninguna. */
export interface PlaybackProgress {
  id: string | null
  elapsedSeconds: number
  /** `NaN` mientras el navegador no sabe lo que dura. */
  durationSeconds: number
}

const SILENT: PlaybackProgress = { id: null, elapsedSeconds: 0, durationSeconds: Number.NaN }

/**
 * Store externo del progreso (`useSyncExternalStore`): cambia varias veces por
 * segundo y solo lo lee la burbuja que suena, no toda la pantalla vía `dispatch`.
 */
export class PlaybackClock {
  private snapshot: PlaybackProgress = SILENT
  private readonly listeners = new Set<() => void>()

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  read = (): PlaybackProgress => this.snapshot

  set(progress: PlaybackProgress): void {
    this.snapshot = progress
    this.listeners.forEach((listener) => listener())
  }

  reset(): void {
    if (this.snapshot !== SILENT) this.set(SILENT)
  }
}

/** Efecto corto (enviado/recibido); si el navegador lo bloquea, no pasa nada. */
export function playSoundEffect(src: string): void {
  if (typeof Audio === 'undefined') return
  new Audio(src).play().catch(() => {})
}

/**
 * Reproduce las notas de voz de Papá Noel en un único `<audio>`: la risa del
 * gesto (si la tiene) y después su voz. Si el navegador bloquea el autoplay,
 * marca el mensaje para que la pantalla ofrezca «▶ Escuchar».
 */
export function useSantaVoicePlayer(dispatch: Dispatch<ChatAction>, speechSource: SpeechSource = fetchSpeech) {
  const player = useMemo(() => new SantaVoicePlayer(dispatch, speechSource), [dispatch, speechSource])

  useEffect(() => () => player.dispose(), [player])

  return useMemo(
    () => ({
      play: (reply: SantaReply) => player.play(reply),
      replay: (id: string, gesture: SantaGesture | null) => player.replay(id, gesture),
      /** El ▶/⏸ de una burbuja: pausa la nota si es la que suena; si no, la repite. */
      toggle: (id: string, gesture: SantaGesture | null) => player.toggle(id, gesture),
      stop: () => player.stop(),
      clock: player.clock,
    }),
    [player]
  )
}

/** Un único `<audio>` y un turno por reproducción: la que empieza deja sin efecto a la anterior. */
class SantaVoicePlayer {
  private audio: HTMLAudioElement | null = null
  /** La voz de cada nota ya oída. */
  private readonly voices = new Map<string, NoteVoice>()
  /** Lo que dura cada cola enlatada ya medida (por URL). */
  private readonly tailDurations = new Map<string, number>()
  /** Las partes de la nota que suena, para el progreso. */
  private span = new NoteSpan(0)
  private run = 0
  /** Nota que suena (risa o voz), o cuya voz se está pidiendo. */
  private currentId: string | null = null
  /** Nota cuya voz (no la risa) está sonando: la que avanza en `clock`. */
  private voiceId: string | null = null
  readonly clock = new PlaybackClock()

  constructor(
    private readonly dispatch: Dispatch<ChatAction>,
    private readonly speechSource: SpeechSource
  ) {}

  private ensureAudio(): HTMLAudioElement {
    if (this.audio) return this.audio
    const audio = new Audio()
    const tick = () => {
      if (this.voiceId === null) return
      this.clock.set({ id: this.voiceId, elapsedSeconds: this.span.elapsed(audio), durationSeconds: this.span.duration(audio) })
    }
    audio.addEventListener('timeupdate', tick)
    audio.addEventListener('durationchange', tick)
    this.audio = audio
    return audio
  }

  async play(reply: SantaReply): Promise<void> {
    const run = ++this.run
    const isCurrent = () => run === this.run
    const audio = this.ensureAudio()
    this.silenceClock()
    this.currentId = reply.id
    if (reply.voice) this.voices.set(reply.id, reply.voice)
    this.span = this.spanFor(reply.voice?.tail ?? null, reply.id, isCurrent)
    const gesture = reply.gesture ?? 'talk'
    this.dispatch({ type: 'playing', id: reply.id, gesture })
    const hooks: StageHooks = {
      onVoiceStarted: () => this.voiceStarted(reply.id, gesture),
      onTailEntered: () => this.span.enterTail(),
      onTailStarted: () => this.tailStarted(reply.id),
    }
    try {
      // Cada etapa comprueba su turno: una reproducción reemplazada no sigue sonando.
      for (const stage of replyStages(reply, gesture, hooks)) {
        if (!isCurrent()) return
        stage.onEnter?.()
        await playToEnd(audio, stage.src, stage.onStarted)
      }
      if (isCurrent()) this.finish({ type: 'playbackEnded' })
    } catch {
      if (isCurrent()) this.finish({ type: 'blocked', id: reply.id })
    }
  }

  /** Las partes de la nota: con cola, su duración se mide (una vez por URL) mientras suena la voz. */
  private spanFor(tail: string | null, id: string, isCurrent: () => boolean): NoteSpan {
    if (tail === null) return new NoteSpan(0)
    const known = this.tailDurations.get(tail)
    if (known !== undefined) return new NoteSpan(known)
    const span = new NoteSpan(Number.NaN)
    void probeDuration(tail).then((seconds) => {
      if (Number.isFinite(seconds)) this.tailDurations.set(tail, seconds)
      if (!isCurrent()) return
      span.tailMeasured(seconds)
      this.reportDuration(id, span.knownDuration())
    })
    return span
  }

  private finish(action: ChatAction): void {
    this.currentId = null
    this.silenceClock()
    this.dispatch(action)
  }

  private silenceClock(): void {
    this.voiceId = null
    this.clock.reset()
  }

  /**
   * Repite un mensaje del historial; si su voz no está en memoria, la pide al
   * servidor (y queda en memoria para la sesión). Si el servidor pide esperar
   * (RATE_LIMITED), Papá Noel «descansa» en vez de sonar mudo.
   */
  async replay(id: string, gesture: SantaGesture | null): Promise<void> {
    const run = ++this.run
    this.currentId = id
    const cached = this.voices.get(id)
    if (cached === undefined) {
      // Lo que sonara se calla mientras llega la voz pedida.
      this.audio?.pause()
      this.silenceClock()
      this.dispatch({ type: 'voiceLoading', id })
    }
    const fetched: FetchedVoice = cached === undefined ? await this.fetchVoice(id) : { kind: 'voice', voice: cached }
    // Un toque a otra nota (o pausa) mientras se pedía esta la deja sin efecto.
    if (run !== this.run) return
    if (fetched.kind === 'rest') {
      this.currentId = null
      return
    }
    await this.play({ id, gesture, voice: fetched.kind === 'voice' ? fetched.voice : null })
  }

  /** Corta lo que suene (p. ej. al borrar la conversación). */
  stop(): void {
    this.run++
    this.currentId = null
    this.audio?.pause()
    this.silenceClock()
  }

  async toggle(id: string, gesture: SantaGesture | null): Promise<void> {
    if (this.currentId !== id) return this.replay(id, gesture)
    this.stop()
    this.dispatch({ type: 'playbackEnded' })
  }

  dispose(): void {
    this.stop()
  }

  /** La voz pedida a su fuente: sin voz si no está disponible; a descansar si la fuente lo pide. */
  private async fetchVoice(id: string): Promise<FetchedVoice> {
    try {
      const speech = await this.speechSource(id)
      const voice = voiceFromBase64(speech.audioBase64, speech.tailAudioUrl ?? null)
      return voice ? { kind: 'voice', voice } : { kind: 'silent' }
    } catch (error) {
      const restUntil = restUntilFrom(error, new Date())
      if (restUntil === null) return { kind: 'silent' }
      this.dispatch({ type: 'rested', until: restUntil })
      return { kind: 'rest' }
    }
  }

  private voiceStarted(id: string, gesture: SantaGesture): void {
    captureEvent('santa_chat_reply_played', { gesture })
    this.voiceId = id
    this.dispatch({ type: 'voiceStarted', id })
    if (this.audio) this.span.headStarted(this.audio)
    const seconds = this.audio ? this.span.duration(this.audio) : Number.NaN
    this.clock.set({ id, elapsedSeconds: 0, durationSeconds: seconds })
    this.reportDuration(id, seconds)
  }

  /** La cola sigue a la voz sin cortar el progreso: arranca donde acabó la voz. */
  private tailStarted(id: string): void {
    if (!this.audio) return
    this.span.tailMeasured(this.audio.duration)
    const seconds = this.span.duration(this.audio)
    this.clock.set({ id, elapsedSeconds: this.span.elapsed(this.audio), durationSeconds: seconds })
    this.reportDuration(id, seconds)
  }

  private reportDuration(id: string, seconds: number): void {
    if (Number.isFinite(seconds)) this.dispatch({ type: 'durationKnown', id, seconds })
  }
}
